import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productionRunService } from '../services/foam/foamServices';
import { getHttpStatus } from '../lib/react-query';
import type { ProductionRunDto } from '../types/foam';

const STORAGE_KEY = 'monarque.activeRunId';

interface ActiveRunContextType {
  activeRunId: number | null;
  activeRun: ProductionRunDto | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  setActiveRunId: (id: number | null) => void;
  refreshRun: () => void;
}

const ActiveRunContext = createContext<ActiveRunContextType | undefined>(undefined);

export const activeRunQueryKey = (id: number | null) => ['productionRun', id] as const;

function readStoredId(): number | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Failed to load production run');
}

export const ActiveRunProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeRunId, setActiveRunIdState] = useState<number | null>(readStoredId);

  const setActiveRunId = useCallback((id: number | null) => {
    setActiveRunIdState(id);
    if (id === null) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, String(id));
    }
  }, []);

  const {
    data: activeRun,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: activeRunQueryKey(activeRunId),
    queryFn: () => productionRunService.getById(activeRunId as number),
    enabled: activeRunId !== null,
    retry: (failureCount, queryError) => {
      const status = getHttpStatus(queryError);
      if (status !== undefined) {
        if (status === 401 || status === 403) return false;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (!isError || activeRunId === null) return;
    if (getHttpStatus(error) === 404) {
      setActiveRunId(null);
    }
  }, [isError, error, activeRunId, setActiveRunId]);

  const refreshRun = useCallback(() => {
    if (activeRunId !== null) void refetch();
  }, [activeRunId, refetch]);

  const value = useMemo(
    () => ({
      activeRunId,
      activeRun,
      isLoading,
      isFetching,
      isError,
      error: isError ? toError(error) : null,
      setActiveRunId,
      refreshRun,
    }),
    [activeRunId, activeRun, isLoading, isFetching, isError, error, setActiveRunId, refreshRun]
  );

  return <ActiveRunContext.Provider value={value}>{children}</ActiveRunContext.Provider>;
};

export const useActiveRun = () => {
  const context = useContext(ActiveRunContext);
  if (!context) throw new Error('useActiveRun must be used within ActiveRunProvider');
  return context;
};
